import React, { useEffect, useMemo, useRef, useState } from 'react'
import Heading from '../../../../components/Heading'
import Text from '../../../../components/Text'
import Select from '../../../../components/Select'
import { useTranslation } from 'react-i18next'
import { useIndividualizationContext } from '../../../../contexts/individualizationContext'
import ArticleCard from './ArticleCard'
import { useFetchData } from '../../../../hooks/useFetchData'
import { Article } from '../../../../types/Article'
import ToggleSwitch from '../../../../components/ToggleSwitch'
import VariationPicker from './VariationPicker'
import { ApiRoutes } from '../../../../services/ApiService'
import MultiSelect, { SelectOption } from '../../../../components/MultiSelect'
import { CustomerGroup } from '../../../../types/CustomerGroup'
import { usePluginContext } from '../../../../contexts/PluginContext'
import { SettingType } from '../../../../types/GlobalSetting'
import { useAdmin } from '../../../../contexts/AdminContext'

const SelectArticle: React.FC = () => {
    const { t } = useTranslation(['individualization'])
    const {
        activeStep,
        article,
        fetchArticle,
        setArticle,
        setChosenVariations,
        chosenVariations,
        setChosenCustomerGroups,
        chosenCustomerGroups,
    } = useIndividualizationContext()
    const {
        getSettingValue
    } = usePluginContext()
    const {
        customerGroups
    } = useAdmin()
    const [search, setSearch] = useState<string>('')
    const [children, setChildren] = useState<'0' | '1'>('1')
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)

    const {
        data: articles,
        isLoading,
        fetch,
    } = useFetchData<Article>(ApiRoutes.ARTICLES)

    const articleOptions: { label: string; value: string }[] = useMemo(() => {
        return articles.map((element: Article) => ({
            label: element.cName + ' (' + element.cArtNr + ')',
            value: element.kArtikel,
        }))
    }, [articles])

    const customerGroupsOptions: { label: string; value: string }[] = useMemo(() => {
        return customerGroups.map((element: CustomerGroup) => ({
            label: element.cName,
            value: element.kKundengruppe.toString(),
        }))
    }, [customerGroups])

    const onVariationChange = (attribute: number, selectedValues: number[]) => {
        setChosenVariations((prev) => {
            return {
                ...prev,
                [`${attribute}`]: selectedValues,
            }
        })
    }

    useEffect(() => {
        clearTimeout(timer.current)
        timer.current = setTimeout(() => {
            fetch({ search, children })
        }, 1250)
    }, [search, children, fetch])

    return activeStep === 0 ? (
        <>
            <Heading level={3} classes={'zr-mt-4'}>
                {t('progress.select_article')}
            </Heading>
            <Text classes={'zr-mt-4'}>{t('select_article.description')}</Text>
            <div className={'zr-d-flex zr-flex-row zr-items-center'}>
                <Select
                    property={'search'}
                    searchable={true}
                    options={articleOptions}
                    onSelect={(_, value) => {
                        if (value.length > 0) {
                            fetchArticle(value)
                        } else {
                            setArticle(null)
                        }
                        setChosenVariations({})
                        setChosenCustomerGroups([])
                    }}
                    value={search}
                    placeholder={t('select_article.search')}
                    onSearchChange={(term) => setSearch(term)}
                    loading={isLoading}
                    classes={'zr-my-6 zr-w-50 zr-mr-4'}
                />
                <ToggleSwitch
                    property={'children'}
                    value={children === '1'}
                    label={
                        children === '1'
                            ? t('select_article.simple_child_articles')
                            : t('select_article.parent_articles')
                    }
                    onChange={(_, value) => setChildren(value ? '1' : '0')}
                />
            </div>
            <Heading level={4}>{t('select_article.selection')}</Heading>
            {article ? (
                <div
                    className={'zr-mt-4'}
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
                >
                    <ArticleCard
                        {...article}
                        handleDelete={() => {
                            setArticle(null)
                            setChosenVariations({})
                            setChosenCustomerGroups([])
                        }}
                    />
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                        }}
                    >
                        {article.variations?.map((variation, index) => (
                            <VariationPicker
                                selectedValues={
                                    chosenVariations[
                                        `${variation.kEigenschaft}`
                                    ] || []
                                }
                                variation={variation}
                                key={index}
                                onSelectionChange={onVariationChange}
                            />
                        ))}
                        {((customerGroups && getSettingValue('activate_customer_group_mode', SettingType.BOOLEAN)) || chosenCustomerGroups.length>0) &&
                            <div style={{gridRow: (article.variations ? 'none' : undefined)}}>
                                <Heading level={4}>{t('select_article.customer_group_text')}</Heading>
                                <MultiSelect
                                    options={customerGroupsOptions}
                                    onChange={(_property: string, value: string, _single: SelectOption) => {setChosenCustomerGroups(value ? value.split(',') : [])}}
                                    property={''}
                                    selected={chosenCustomerGroups}
                                    placeholder={t('select_article.customer_group_selection')}
                                    className={'zr-mt-3'}
                                />
                            </div>
                        }
                    </div>
                </div>
            ) : (
                <Text>{t('select_article.no_selection')}</Text>
            )}
        </>
    ) : null
}

export default SelectArticle
